import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { registerEnum } from '@adornis/baseql/metadata/register.js';
import { getContext } from '@adornis/baseql/server/context.js';
import { getContextFromSessionID } from '@adornis/baseql/server/getContext.js';
import { AdornisUser } from '@adornis/users/db/a-user.js';
import type { UserPermission } from '@adornis/users/db/types.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import type { InsertOneResult } from 'mongodb';
import type { MatchingPermission } from '../permissions.js';
import { MatchingCountry, MatchingLanguage } from './auto-matching-fields.js';

/**
 * @entity BuddyUser
 */
@Entity()
export abstract class BuddyUser extends AdornisUser<UserPermission | MatchingPermission> {
  static override _class = 'BuddyUser';

  constructor(doc: PartialEntityData<BuddyUser>) {
    super(doc);
  }

  @validate(nonOptional())
  @Field(type => ID)
  timeframeID: Maybe<string>;

  @Field(type => String)
  note: Maybe<string>;

  static override async beforeCreate(entity: BuddyUser) {
    entity.createdAt = new Date();
    await super.beforeCreate(entity);
  }

  static override async afterCreate(user: BuddyUserMentor, result: InsertOneResult) {
    await super.afterCreate(user, result);

    const _context = getContext();
    const context = await (_context ? getContextFromSessionID(_context.sessionID) : null);
    if (context && context?.userClass?.includes('Mentor')) {
      logger.info({ userID: user._id }, 'Mentor reregistration context switch');
      context.userID = user._id;
      context.userClass = user._class;
      context.impersonatingUser = undefined;
      context.impersonatingUserClass = undefined;
      await context.recreate();
    }
  }

  static getMailVariables(user: BuddyUser): [string, string][] {
    return [];
  }
}

/**
 * @entity BuddyUserMentor
 */
@Entity()
export class BuddyUserMentor extends BuddyUser {
  static override _class = 'BuddyUserMentor';

  // TODO: fix that
  // @DataLoadedField(type => [Match], ({ _id }) => ({ mentorID: _id }))
  // matches: Maybe<Match[]>;
}

/**
 * @entity BuddyUserMentee
 */
@Entity()
export class BuddyUserMentee extends BuddyUser {
  static override _class = 'BuddyUserMentee';

  // TODO: fix that
  // @DataLoadedField(type => [Match], ({ _id }) => ({ menteeID: _id }))
  // matches: Maybe<Match[]>;
}

export enum GenderPreference {
  NO_PREFERENCE = 'NO_PREFERENCE',
  SAME_SEX = 'SAME_SEX',
}

registerEnum({ enum: GenderPreference, name: 'GenderPreference' });

export enum Gender {
  MALE = 'MALE',
  FEMALE = 'FEMALE',
  DIVERSE = 'DIVERSE',
  NOT_SPECIFIED = 'NOT_SPECIFIED',
}

registerEnum({ enum: Gender, name: 'Gender' });

registerEnum({ enum: MatchingLanguage, name: 'MatchingLanguage' });
registerEnum({ enum: MatchingCountry, name: 'MatchingCountry' });

/**
 * @entity DefaultMentorUser
 */
@Entity()
export class DefaultMentorUser extends BuddyUserMentor {
  static override _class = 'DefaultMentorUser';

  constructor(doc: PartialEntityData<DefaultMentorUser>) {
    super(doc);
  }

  @validate(nonOptional())
  @Field(type => Gender)
  gender: Maybe<Gender>;

  @validate(nonOptional())
  @Field(type => GenderPreference)
  genderPreference: Maybe<GenderPreference>;

  @Field(type => [MatchingLanguage])
  foreignLanguages: Maybe<MatchingLanguage[]>;

  @Field(type => MatchingLanguage)
  nativeLanguage: Maybe<MatchingLanguage>;
}

/**
 * @entity DefaultMenteeUser
 */
@Entity()
export class DefaultMenteeUser extends BuddyUserMentee {
  static override _class = 'DefaultMenteeUser';

  constructor(doc: PartialEntityData<DefaultMenteeUser>) {
    super(doc);
  }

  @validate(nonOptional())
  @Field(type => Gender)
  gender: Maybe<Gender>;

  @validate(nonOptional())
  @Field(type => GenderPreference)
  genderPreference: Maybe<GenderPreference>;

  @Field(type => [MatchingLanguage])
  foreignLanguages: Maybe<MatchingLanguage[]>;

  @Field(type => MatchingLanguage)
  nativeLanguage: Maybe<MatchingLanguage>;
}
